import { computed, Injectable, signal } from '@angular/core';
import { AIChatContextGroup, AIChatContextItem } from './models';

@Injectable({
	providedIn: 'root',
})
export class AIChatContextService {
	private groupsSig = signal<AIChatContextGroup<any>[]>([]);

	/** Reactive read of all registered non-vehicle groups. */
	public groups = computed(() => this.groupsSig());

	/**
	 * Register a new group for a denotation channel other than '@'.
	 * Returns a cleanup function that deregisters the group.
	 */
	registerGroup<T>(group: AIChatContextGroup<T>): () => void {
		if (group.mentionChannel === '@') {
			return () => void 0;
		}

		const dupChannel = this.groupsSig().some((g) => g.mentionChannel === group.mentionChannel);
		if (dupChannel) {
			return () => void 0;
		}

		const dupKey = this.groupsSig().some((g) => g.key === group.key);
		if (dupKey) {
			return () => void 0;
		}

		this.groupsSig.set([...this.groupsSig(), group]);
		return () => this.deregisterGroup(group.key);
	}

	/** Deregister a group by key. */
	deregisterGroup(groupKey: string): void {
		this.groupsSig.set(this.groupsSig().filter((g) => g.key !== groupKey));
	}

	/**
	 * Add items to an existing group. Returns a cleanup function that removes the added items.
	 */
	addItemToGroup<T>(groupKey: string, ...items: AIChatContextItem<T>[]): () => void {
		const list = this.groupsSig();
		const idx = list.findIndex((g) => g.key === groupKey);
		if (idx < 0) return () => void 0;

		const g = list[idx] as AIChatContextGroup<T>;
		const nextGroup: AIChatContextGroup<T> = { ...g, items: [...g.items, ...items] };
		const next = list.slice();
		next[idx] = nextGroup as AIChatContextGroup<any>;
		this.groupsSig.set(next);

		return () => {
			const cur = this.groupsSig();
			const i = cur.findIndex((x) => x.key === groupKey);
			if (i < 0) return;
			const grp = cur[i] as AIChatContextGroup<T>;
			const remaining = grp.items.filter((it) => !items.includes(it));
			const rep: AIChatContextGroup<T> = { ...grp, items: remaining };
			const out = cur.slice();
			out[i] = rep as AIChatContextGroup<any>;
			this.groupsSig.set(out);
		};
	}

	/**
	 * Build the extra (non-'@') mention config section that AIChatInput can merge with its vehicle config.
	 */
	buildExtraMentionConfig() {
		const groups = this.groups();
		const chars = groups.map((g) => g.mentionChannel);

		return {
			mentionDenotationChars: chars,
			source: (term: string, renderList: Function, denotationChar: string) => {
				const group = groups.find((g) => g.mentionChannel === denotationChar);
				if (!group) return renderList([], term);
				const t = term.toLowerCase();
				const items = group.items
					.filter((it) => (it.searchValue || '').toLowerCase().includes(t))
					.map((it) => ({
						id: it.searchValue,
						value: it.toLabel(),
						sourceItem: it,
						denotationChar,
					}));
				renderList(items, term);
			},
			renderItem: (item: any) => `${item.value}`,
			onSelect: (item: any, insertItem: Function) => insertItem(item),
		} as const;
	}
}
